import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { API_HOST } from '../../config/api.config';
import { 
  Building2, 
  Search, 
  Download, 
  Eye, 
  FileText, 
  ChevronLeft, 
  ChevronRight,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  X,
  Truck,
  Phone,
  Mail,
  Calendar,
  MapPin,
  ExternalLink,
  Paperclip
} from 'lucide-react';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  
  input {
    padding: 10px 16px 10px 40px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    width: 300px;
    transition: all 0.2s;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
  }
  
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'secondary':
        return `
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
          &:hover { background: #f8fafc; }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          border: none;
          &:hover { background: #059669; }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          border: none;
          &:hover { background: #dc2626; }
        `;
      default:
        return `
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          border: none;
          &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div<{ color: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.color};
  
  h3 {
    font-size: 32px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
  
  p {
    font-size: 14px;
    color: #64748b;
    margin: 4px 0 0 0;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  th {
    background: #f8fafc;
    font-weight: 600;
    color: #475569;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  td {
    font-size: 14px;
    color: #334155;
  }
  
  tbody tr:hover {
    background: #f8fafc;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  
  ${({ status }) => {
    switch (status) {
      case 'VALIDADO':
        return 'background: #dcfce7; color: #166534;';
      case 'RECHAZADO':
        return 'background: #fee2e2; color: #991b1b;';
      case 'ENVIADO':
        return 'background: #dbeafe; color: #1e40af;';
      default:
        return 'background: #f3f4f6; color: #4b5563;';
    }
  }}
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #e2e8f0;
`;

const PaginationInfo = styled.span`
  font-size: 14px;
  color: #64748b;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? '#6366f1' : '#e2e8f0'};
  background: ${props => props.active ? '#6366f1' : 'white'};
  color: ${props => props.active ? 'white' : '#64748b'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#6366f1' : '#f8fafc'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 8px;
  border-radius: 8px;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #e2e8f0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const DetailItem = styled.div`
  label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  
  span {
    font-size: 14px;
    color: #1e293b;
  }
  
  .yes { color: #059669; font-weight: 500; }
  .no { color: #dc2626; }
`;

// Contenedor para tags/badges que permite mejor visualización de listas
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Tag = styled.span<{ $variant?: 'default' | 'tech' | 'capacity' | 'vehicle' }>`
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  
  ${({ $variant }) => {
    switch ($variant) {
      case 'tech':
        return `
          background: #ede9fe;
          color: #7c3aed;
          border: 1px solid #c4b5fd;
        `;
      case 'capacity':
        return `
          background: #dbeafe;
          color: #2563eb;
          border: 1px solid #93c5fd;
        `;
      case 'vehicle':
        return `
          background: #dcfce7;
          color: #16a34a;
          border: 1px solid #86efac;
        `;
      default:
        return `
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #64748b;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  
  svg {
    margin-bottom: 16px;
    color: #cbd5e1;
  }
  
  h3 {
    font-size: 18px;
    margin: 0 0 8px 0;
    color: #475569;
  }
  
  p {
    margin: 0;
  }
`;

// ============================================================================
// INTERFACES
// ============================================================================

interface Proveedor {
  id: number;
  proveedor_nombre: string;
  proveedor_nit: string;
  username: string;
  estado: string;
  contacto1_nombre: string | null;
  contacto1_celular: string | null;
  contacto1_correo: string | null;
  contacto2_nombre: string | null;
  contacto2_celular: string | null;
  contacto2_correo: string | null;
  anios_experiencia: string | null;
  conocimiento_transporte_alimentos: boolean;
  ha_cargado_en_alianza: boolean;
  numero_vehiculos: number | null;
  porcentaje_flota_propia: string | null;
  edad_promedio_flota: string | null;
  tecnologias: string | null;
  capacidades_carga: string | null;
  tipos_vehiculo: string | null;
  tipos_vehiculo_otro: string | null;
  // Brochure
  brochure_url: string | null;
  brochure_archivo: string | null;
  // Plan de descarbonización
  plan_descarbonizacion_existe: boolean;
  plan_descarbonizacion_url: string | null;
  plan_descarbonizacion_archivo: string | null;
  capacitacion_conductores: boolean;
  capacitacion_carga_alimentos: boolean;
  medicion_huella_carbono: boolean;
  gps_sistema: string | null;
  uso_peajes_electronicos: string | null;
  base_soporte_prueba_entrega: boolean;
  cuenta_optimizador_ruta: boolean;
  // Certificaciones adicionales
  tiene_otra_certificacion: boolean;
  otra_certificacion: string | null;
  soporte_otra_certificacion: string | null;
  otras_certificaciones: string | null;
  permisos_puertos: string | null;
  poliza_limite_trayecto: number | null;
  monto_poliza_RCE: number | null;
  empresa_contingencia_granel: string | null;
  capacidad_ultima_milla: boolean;
  ciudades_ultima_milla: string | null;
  rutas_compensadas: boolean;
  rutas_compensadas_cuales: string | null;
  observaciones: string | null;
  creado_en: string;
  actualizado_en: string | null;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

const ProveedoresModule: React.FC = () => {
  // State
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [selectedProveedor, setSelectedProveedor] = useState<Proveedor | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // API Base URL (sin /api, ya que los endpoints lo incluyen)
  const API_BASE_URL = API_HOST;

  // Función helper para renderizar tags de una lista separada por comas
  const renderTags = (value: string | null, variant: 'default' | 'tech' | 'capacity' | 'vehicle' = 'default') => {
    if (!value) return <span>-</span>;
    
    // Separar por comas y limpiar espacios
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    
    if (items.length === 0) return <span>-</span>;
    
    return (
      <TagsContainer>
        {items.map((item, index) => (
          <Tag key={index} $variant={variant}>
            {item.replace(/_/g, ' ')}
          </Tag>
        ))}
      </TagsContainer>
    );
  };

  // Fetch proveedores
  const fetchProveedores = async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '25',
        ...(searchTerm && { search: searchTerm }),
        ...(estadoFilter && { estado: estadoFilter })
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/proveedores?${params}`);
      const data = await response.json();

      if (response.ok) {
        setProveedores(data.proveedores);
        setPagination(data.pagination);
      } else {
        console.error('Error fetching proveedores:', data.error);
      }
    } catch (error) {
      console.error('Error fetching proveedores:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and search/filter changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchProveedores(1);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, estadoFilter]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    fetchProveedores(newPage);
  };

  // Open detail modal
  const handleViewDetails = async (proveedor: Proveedor) => {
    setSelectedProveedor(proveedor);
    setIsModalOpen(true);
  };

  // Función genérica para descargar archivos de GCS
  const handleDownloadFile = async (archivo: string, folderPrefix: string) => {
    if (!archivo) return;
    
    setIsDownloading(true);
    try {
      // El archivo puede venir como nombre simple o con la ruta completa
      // Asegurar que tenga el prefijo correcto
      const blobName = archivo.startsWith(`${folderPrefix}/`) 
        ? archivo 
        : `${folderPrefix}/${archivo}`;
      
      const response = await fetch(
        `${API_BASE_URL}/api/admin/proveedores/descargar-archivo?blob_name=${encodeURIComponent(blobName)}`
      );

      if (response.ok) {
        // Obtener el nombre del archivo del header Content-Disposition o usar uno por defecto
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = archivo.split('/').pop() || 'archivo.pdf';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            filename = match[1].replace(/['"]/g, '');
          }
        }
        
        // Crear blob y descargar
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Si hay error, intentar leer el JSON de error
        const data = await response.json();
        alert('Error al descargar: ' + (data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error al descargar el archivo');
    } finally {
      setIsDownloading(false);
    }
  };

  // Download plan de descarbonización (mantener para compatibilidad)
  const handleDownloadPlan = async (archivo: string) => {
    await handleDownloadFile(archivo, 'planes_descarbonizacion');
  };

  // Download brochure
  const handleDownloadBrochure = async (archivo: string) => {
    await handleDownloadFile(archivo, 'brochures');
  };

  // Download certificación
  const handleDownloadCertificacion = async (archivo: string) => {
    await handleDownloadFile(archivo, 'otras_certificaciones');
  };

  // Abrir URL externa
  const handleOpenUrl = (url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Verificar si es una URL o un archivo subido
  const isUrl = (value: string | null): boolean => {
    if (!value) return false;
    return value.startsWith('http://') || value.startsWith('https://');
  };

  // Update estado
  const handleUpdateEstado = async (proveedorId: number, nuevoEstado: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/proveedores/${proveedorId}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado })
      });

      if (response.ok) {
        // Refresh the list
        fetchProveedores(pagination.page);
        // Update modal if open
        if (selectedProveedor && selectedProveedor.id === proveedorId) {
          setSelectedProveedor({ ...selectedProveedor, estado: nuevoEstado });
        }
      } else {
        const data = await response.json();
        alert('Error al actualizar estado: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating estado:', error);
      alert('Error al actualizar el estado');
    }
  };

  // Export to Excel/CSV
  const handleExport = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/proveedores/exportar`);
      const data = await response.json();

      if (response.ok) {
        // Create CSV content
        const headers = [
          'ID', 'Nombre', 'NIT', 'Usuario', 'Estado', 
          'Contacto 1', 'Tel 1', 'Email 1',
          'Años Exp.', 'Vehículos', 'Plan Desc.', 'Fecha Registro'
        ];
        
        const rows = data.proveedores.map((p: Proveedor) => [
          p.id,
          p.proveedor_nombre,
          p.proveedor_nit,
          p.username,
          p.estado,
          p.contacto1_nombre || '',
          p.contacto1_celular || '',
          p.contacto1_correo || '',
          p.anios_experiencia || '',
          p.numero_vehiculos || '',
          p.plan_descarbonizacion_existe ? 'Sí' : 'No',
          p.creado_en ? new Date(p.creado_en).toLocaleDateString() : ''
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row: (string | number)[]) => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Download CSV
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `proveedores_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Error al exportar datos');
    }
  };

  // Calculate stats
  const stats = {
    total: pagination.total,
    validados: proveedores.filter(p => p.estado === 'VALIDADO').length,
    pendientes: proveedores.filter(p => p.estado === 'BORRADOR' || p.estado === 'ENVIADO').length,
    rechazados: proveedores.filter(p => p.estado === 'RECHAZADO').length
  };

  // Render status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'VALIDADO': return <CheckCircle size={14} />;
      case 'RECHAZADO': return <XCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  // Format boolean
  const formatBoolean = (value: boolean | undefined | null) => {
    if (value === true) return <span className="yes">✓ Sí</span>;
    return <span className="no">✗ No</span>;
  };

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>
          <Building2 size={32} />
          Gestión de Proveedores
        </Title>
        <ActionButton variant="secondary" onClick={handleExport}>
          <Download size={16} />
          Exportar CSV
        </ActionButton>
      </Header>

      {/* Stats - Solo total registrados */}
      <StatsGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', maxWidth: '300px' }}>
        <StatCard color="#6366f1">
          <h3>{pagination.total}</h3>
          <p>Total Proveedores Registrados</p>
        </StatCard>
      </StatsGrid>

      {/* Search */}
      <SearchBar>
        <SearchInput>
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar por nombre, NIT, usuario o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <ActionButton variant="secondary" onClick={() => fetchProveedores(pagination.page)}>
          <RefreshCw size={16} />
          Actualizar
        </ActionButton>
      </SearchBar>

      {/* Table */}
      <TableContainer style={{ marginTop: '24px' }}>
        {isLoading ? (
          <LoadingSpinner>
            <RefreshCw size={32} />
          </LoadingSpinner>
        ) : proveedores.length === 0 ? (
          <EmptyState>
            <Building2 size={48} />
            <h3>No hay proveedores registrados</h3>
            <p>Los proveedores aparecerán aquí cuando se registren</p>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>NIT</th>
                  <th>Contacto</th>
                  <th>Experiencia</th>
                  <th>Vehículos</th>
                  <th>Plan Desc.</th>
                  <th>Fecha Registro</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proveedores.map((prov) => (
                  <tr key={prov.id}>
                    <td>
                      <strong>{prov.proveedor_nombre}</strong>
                      <br />
                      <small style={{ color: '#64748b' }}>{prov.username}</small>
                    </td>
                    <td>{prov.proveedor_nit}</td>
                    <td>
                      {prov.contacto1_nombre ? (
                        <>
                          {prov.contacto1_nombre}
                          <br />
                          <small style={{ color: '#64748b' }}>{prov.contacto1_correo || prov.contacto1_celular}</small>
                        </>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td>{prov.anios_experiencia || '-'}</td>
                    <td>{prov.numero_vehiculos || '-'}</td>
                    <td>
                      {prov.plan_descarbonizacion_archivo ? (
                        <ActionButton 
                          variant="secondary" 
                          onClick={() => handleDownloadPlan(prov.plan_descarbonizacion_archivo!)}
                          disabled={isDownloading}
                          style={{ padding: '6px 10px', fontSize: '12px' }}
                        >
                          <FileText size={14} />
                          Descargar
                        </ActionButton>
                      ) : prov.plan_descarbonizacion_existe ? (
                        <span style={{ color: '#10b981' }}>✓ Tiene</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>No tiene</span>
                      )}
                    </td>
                    <td>
                      {prov.creado_en ? new Date(prov.creado_en).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <ActionButton
                        variant="secondary"
                        onClick={() => handleViewDetails(prov)}
                        style={{ padding: '6px 10px', fontSize: '12px' }}
                      >
                        <Eye size={14} />
                        Ver Detalle
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            <Pagination>
              <PaginationInfo>
                Mostrando {proveedores.length} de {pagination.total} proveedores
              </PaginationInfo>
              <PaginationButtons>
                <PageButton
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                >
                  <ChevronLeft size={16} />
                </PageButton>
                <PageButton active>
                  {pagination.page} / {pagination.total_pages}
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                >
                  <ChevronRight size={16} />
                </PageButton>
              </PaginationButtons>
            </Pagination>
          </>
        )}
      </TableContainer>

      {/* Detail Modal */}
      {isModalOpen && selectedProveedor && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <Building2 size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                {selectedProveedor.proveedor_nombre}
              </ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Información básica */}
              <DetailSection>
                <h3>Información de la Empresa</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>NIT</label>
                    <span>{selectedProveedor.proveedor_nit}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Usuario</label>
                    <span>{selectedProveedor.username}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Años de Experiencia</label>
                    <span>{selectedProveedor.anios_experiencia || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Conoce Transporte de Alimentos</label>
                    {formatBoolean(selectedProveedor.conocimiento_transporte_alimentos)}
                  </DetailItem>
                  <DetailItem>
                    <label>Ha Cargado en Alianza</label>
                    {formatBoolean(selectedProveedor.ha_cargado_en_alianza)}
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Contactos */}
              <DetailSection>
                <h3>Información de Contacto</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>Contacto Principal</label>
                    <span>{selectedProveedor.contacto1_nombre || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Teléfono</label>
                    <span>{selectedProveedor.contacto1_celular || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Correo</label>
                    <span>{selectedProveedor.contacto1_correo || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Contacto Secundario</label>
                    <span>{selectedProveedor.contacto2_nombre || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Teléfono 2</label>
                    <span>{selectedProveedor.contacto2_celular || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Correo 2</label>
                    <span>{selectedProveedor.contacto2_correo || '-'}</span>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Flota */}
              <DetailSection>
                <h3>Información de Flota</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>Número de Vehículos</label>
                    <span>{selectedProveedor.numero_vehiculos || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>% Flota Propia</label>
                    <span>{selectedProveedor.porcentaje_flota_propia || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Edad Promedio Flota</label>
                    <span>{selectedProveedor.edad_promedio_flota || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Tecnologías</label>
                    {renderTags(selectedProveedor.tecnologias, 'tech')}
                  </DetailItem>
                  <DetailItem>
                    <label>Capacidades de Carga</label>
                    {renderTags(selectedProveedor.capacidades_carga, 'capacity')}
                  </DetailItem>
                  <DetailItem>
                    <label>Tipos de Vehículo</label>
                    {renderTags(selectedProveedor.tipos_vehiculo, 'vehicle')}
                  </DetailItem>
                  {selectedProveedor.tipos_vehiculo_otro && (
                    <DetailItem>
                      <label>Otro Tipo de Vehículo</label>
                      <span>{selectedProveedor.tipos_vehiculo_otro}</span>
                    </DetailItem>
                  )}
                </DetailGrid>
              </DetailSection>

              {/* Documentos y Adjuntos */}
              <DetailSection>
                <h3>📎 Documentos y Adjuntos</h3>
                <DetailGrid>
                  {/* Brochure */}
                  <DetailItem>
                    <label>Brochure de la Empresa</label>
                    {selectedProveedor.brochure_archivo || selectedProveedor.brochure_url ? (
                      isUrl(selectedProveedor.brochure_url) && !selectedProveedor.brochure_archivo ? (
                        <ActionButton 
                          variant="secondary"
                          onClick={() => handleOpenUrl(selectedProveedor.brochure_url!)}
                          style={{ marginTop: '4px' }}
                        >
                          <ExternalLink size={16} />
                          Ver URL
                        </ActionButton>
                      ) : (
                        <ActionButton 
                          variant="primary"
                          onClick={() => handleDownloadBrochure(selectedProveedor.brochure_archivo || selectedProveedor.brochure_url!)}
                          disabled={isDownloading}
                          style={{ marginTop: '4px' }}
                        >
                          <Download size={16} />
                          Descargar Brochure
                        </ActionButton>
                      )
                    ) : (
                      <span style={{ color: '#94a3b8' }}>No adjuntado</span>
                    )}
                  </DetailItem>

                  {/* Plan de Descarbonización */}
                  <DetailItem>
                    <label>Plan de Descarbonización</label>
                    {selectedProveedor.plan_descarbonizacion_archivo || selectedProveedor.plan_descarbonizacion_url ? (
                      isUrl(selectedProveedor.plan_descarbonizacion_url) && !selectedProveedor.plan_descarbonizacion_archivo ? (
                        <ActionButton 
                          variant="secondary"
                          onClick={() => handleOpenUrl(selectedProveedor.plan_descarbonizacion_url!)}
                          style={{ marginTop: '4px' }}
                        >
                          <ExternalLink size={16} />
                          Ver URL
                        </ActionButton>
                      ) : (
                        <ActionButton 
                          variant="primary"
                          onClick={() => handleDownloadPlan(selectedProveedor.plan_descarbonizacion_archivo || selectedProveedor.plan_descarbonizacion_url!)}
                          disabled={isDownloading}
                          style={{ marginTop: '4px' }}
                        >
                          <Download size={16} />
                          Descargar Plan
                        </ActionButton>
                      )
                    ) : selectedProveedor.plan_descarbonizacion_existe ? (
                      <span style={{ color: '#10b981' }}>✓ Tiene (sin archivo)</span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>No tiene</span>
                    )}
                  </DetailItem>

                  {/* Certificaciones Adicionales */}
                  <DetailItem>
                    <label>Certificación Adicional</label>
                    {selectedProveedor.tiene_otra_certificacion ? (
                      <>
                        <span style={{ color: '#10b981', display: 'block' }}>✓ Sí tiene</span>
                        {selectedProveedor.otra_certificacion && (
                          <span style={{ display: 'block', marginTop: '4px', fontSize: '13px' }}>
                            {selectedProveedor.otra_certificacion}
                          </span>
                        )}
                        {selectedProveedor.soporte_otra_certificacion && (
                          isUrl(selectedProveedor.soporte_otra_certificacion) ? (
                            <ActionButton 
                              variant="secondary"
                              onClick={() => handleOpenUrl(selectedProveedor.soporte_otra_certificacion!)}
                              style={{ marginTop: '8px' }}
                            >
                              <ExternalLink size={16} />
                              Ver URL Soporte
                            </ActionButton>
                          ) : (
                            <ActionButton 
                              variant="primary"
                              onClick={() => handleDownloadCertificacion(selectedProveedor.soporte_otra_certificacion!)}
                              disabled={isDownloading}
                              style={{ marginTop: '8px' }}
                            >
                              <Download size={16} />
                              Descargar Soporte
                            </ActionButton>
                          )
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>No tiene</span>
                    )}
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Sostenibilidad */}
              <DetailSection>
                <h3>🌱 Sostenibilidad y Capacitaciones</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>Capacitación Conductores</label>
                    {formatBoolean(selectedProveedor.capacitacion_conductores)}
                  </DetailItem>
                  <DetailItem>
                    <label>Capacitación Carga Alimentos</label>
                    {formatBoolean(selectedProveedor.capacitacion_carga_alimentos)}
                  </DetailItem>
                  <DetailItem>
                    <label>Medición Huella de Carbono</label>
                    {formatBoolean(selectedProveedor.medicion_huella_carbono)}
                  </DetailItem>
                  <DetailItem>
                    <label>GPS/Sistema</label>
                    <span>{selectedProveedor.gps_sistema || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Peajes Electrónicos</label>
                    <span>{selectedProveedor.uso_peajes_electronicos || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Soporte Prueba de Entrega</label>
                    {formatBoolean(selectedProveedor.base_soporte_prueba_entrega)}
                  </DetailItem>
                  <DetailItem>
                    <label>Optimizador de Ruta</label>
                    {formatBoolean(selectedProveedor.cuenta_optimizador_ruta)}
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              {/* Otros */}
              <DetailSection>
                <h3>💼 Información Adicional</h3>
                <DetailGrid>
                  <DetailItem>
                    <label>Permisos Puertos</label>
                    <span>{selectedProveedor.permisos_puertos || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Póliza Límite Trayecto</label>
                    <span>
                      {selectedProveedor.poliza_limite_trayecto 
                        ? `$${selectedProveedor.poliza_limite_trayecto.toLocaleString()}`
                        : '-'}
                    </span>
                  </DetailItem>
                  <DetailItem>
                    <label>Monto Póliza RCE</label>
                    <span>
                      {selectedProveedor.monto_poliza_RCE 
                        ? `$${selectedProveedor.monto_poliza_RCE.toLocaleString()}`
                        : '-'}
                    </span>
                  </DetailItem>
                  <DetailItem>
                    <label>Empresa Contingencia Granel</label>
                    <span>{selectedProveedor.empresa_contingencia_granel || '-'}</span>
                  </DetailItem>
                  <DetailItem>
                    <label>Capacidad Última Milla</label>
                    {formatBoolean(selectedProveedor.capacidad_ultima_milla)}
                  </DetailItem>
                  {selectedProveedor.ciudades_ultima_milla && (
                    <DetailItem>
                      <label>Ciudades Última Milla</label>
                      <span>{selectedProveedor.ciudades_ultima_milla}</span>
                    </DetailItem>
                  )}
                  <DetailItem>
                    <label>Rutas Compensadas</label>
                    {formatBoolean(selectedProveedor.rutas_compensadas)}
                  </DetailItem>
                  {selectedProveedor.rutas_compensadas_cuales && (
                    <DetailItem>
                      <label>Cuáles Rutas</label>
                      <span>{selectedProveedor.rutas_compensadas_cuales}</span>
                    </DetailItem>
                  )}
                  <DetailItem>
                    <label>Fecha de Registro</label>
                    <span>
                      {selectedProveedor.creado_en 
                        ? new Date(selectedProveedor.creado_en).toLocaleString()
                        : '-'}
                    </span>
                  </DetailItem>
                </DetailGrid>
                {selectedProveedor.observaciones && (
                  <DetailItem style={{ marginTop: '16px' }}>
                    <label>Observaciones</label>
                    <span style={{ display: 'block', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                      {selectedProveedor.observaciones}
                    </span>
                  </DetailItem>
                )}
              </DetailSection>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ProveedoresModule;

